import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { API_URL } from './AdminShared';
import logger from '../../utils/logger';

export const MyAccountTab = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/me`);
        setName(res.data.name || '');
        setEmail(res.data.email || '');
      } catch (err) { logger.error('Failed to load profile:', err); }
      setLoading(false);
    };
    fetchMe();
  }, []);

  const handleSave = async () => {
    if (!currentPassword) {
      toast.error('Enter your current password to make changes');
      return;
    }
    if (newPassword && newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword && newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      const payload = { current_password: currentPassword };
      if (name) payload.name = name;
      if (email) payload.email = email;
      if (newPassword) payload.new_password = newPassword;

      const res = await axios.put(`${API_URL}/api/auth/update-profile`, payload);
      toast.success(res.data.message);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6 max-w-xl" data-testid="my-account-tab">
      {loading ? <div className="text-center py-8 text-slate-400">Loading...</div> : <>
      <div>
        <h2 className="text-xl font-semibold text-[#0F172A]">My Account</h2>
        <p className="text-sm text-slate-500 mt-1">Update your admin email, name, or password.</p>
      </div>

      <div className="luxury-card p-6 space-y-5">
        <div>
          <label className="text-xs text-slate-500 tracking-wider uppercase font-medium block mb-1">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 px-4 py-2.5 text-sm text-slate-700 focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none"
            data-testid="account-name"
          />
        </div>

        <div>
          <label className="text-xs text-slate-500 tracking-wider uppercase font-medium block mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 px-4 py-2.5 text-sm text-slate-700 focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none"
            data-testid="account-email"
          />
        </div>

        <hr className="border-slate-100" />

        <div>
          <label className="text-xs text-slate-500 tracking-wider uppercase font-medium block mb-1">New Password <span className="text-slate-400">(leave blank to keep current)</span></label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            className="w-full bg-slate-50 border border-slate-200 px-4 py-2.5 text-sm text-slate-700 focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none"
            data-testid="account-new-password"
          />
        </div>

        {newPassword && (
          <div>
            <label className="text-xs text-slate-500 tracking-wider uppercase font-medium block mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="w-full bg-slate-50 border border-slate-200 px-4 py-2.5 text-sm text-slate-700 focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none"
              data-testid="account-confirm-password"
            />
          </div>
        )}

        <hr className="border-slate-100" />

        <div>
          <label className="text-xs text-red-500 tracking-wider uppercase font-medium block mb-1">Current Password <span className="text-red-400">(required to save changes)</span></label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Enter your current password"
            className="w-full bg-slate-50 border border-red-200 px-4 py-2.5 text-sm text-slate-700 focus:border-[#E07A3A] focus:ring-1 focus:ring-[#E07A3A] outline-none"
            data-testid="account-current-password"
          />
        </div>

        <Button
          onClick={handleSave}
          disabled={saving || !currentPassword}
          className="bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs tracking-wider px-8 py-5 w-full"
          data-testid="save-account-btn"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
      </>}
    </div>
  );
};
